<template>
  <SfCardInline
    :ui-state="uiState"
    :variant="variant"
    data-testid="document-card"
  >
    <template #left>
      <div class="tw-flex tw-items-center tw-gap-1">
        <div class="tw-flex tw-items-center" :class="documentNameClasses">
          <SfEllipsis
            :length="documentNameEllipsisLength"
            :tooltip="document.name"
            >{{ document.name }}</SfEllipsis
          >
        </div>
        <i
          v-if="iconClass"
          v-tooltip="errorMessages"
          class="fa text-sm"
          :class="iconClass"
        ></i>
      </div>
    </template>
    <template #right>
      <template v-if="isActivelyDownloading">
        <div class="tw-flex tw-rounded-full tw-h-2 tw-bg-gray-200 tw-w-[80px]">
          <div
            :style="{ width: progress + '%' }"
            class="tw-rounded-full tw-bg-sf-primary-light"
          ></div>
        </div>
        <div
          class="
            tw-flex tw-w-[30px] tw-text-[11px] tw-font-medium tw-justify-end
          "
        >
          {{ progress }}%
        </div>
      </template>
      <div class="tw-flex tw-items-center tw-gap-2">
        <SfEllipsis
          :class="documentSizeClasses"
          class="tw-text-right"
          :length="documentSizeEllipsisLength"
          :tooltip="secondaryText"
          >{{ secondaryText }}</SfEllipsis
        >
        <div class="tw-flex tw-gap-[2px]">
          <i
            v-if="viewingId && allowDownload"
            class="
              sf-pointer
              fa fa-cloud-download
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-download"
            @click="handleDownload"
          ></i>
          <i
            v-if="viewingUrl"
            class="
              sf-pointer
              fa fa-eye
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-view"
            @click="handleView"
          ></i>
          <i
            v-if="allowDelete"
            class="
              sf-pointer
              fa fa-trash
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-delete"
            @click="handleDelete"
          ></i>
        </div>
      </div>
    </template>
  </SfCardInline>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import type { ColorVariantsType, UIStateType } from '@app/shared/constants'
import SfCardInline from '@app/shared/vue/components/sf-card/sf-card-inline.vue'
import SfEllipsis from '@app/shared/vue/components/sf-ellipsis/sf-ellipsis.vue'
import { ColorVariants, UIState } from '@app/shared/constants'
import { isDirectUploadDocument } from '@app/shared/predicates'
import { formatBytes } from '@app/utils/file-helpers'
import type { StoredDocument } from '../types'
import { isNotProposedDocument } from '../predicates'
import { useDocumentView } from '../composables/use-document-view'

interface Props {
  allowDelete?: boolean
  allowDownload?: boolean
  document: StoredDocument
  progress?: string
  uiState?: Extract<UIStateType, 'default' | 'loading'>
  variant?: ColorVariantsType
  errorMessages?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  allowDelete: true,
  allowDownload: true,
  errorMessages: () => [],
  progress: undefined,
  uiState: UIState.DEFAULT,
  variant: ColorVariants.DEFAULT,
})

const emit =
  defineEmits<{
    delete: [value: number | string]
  }>()

const documentNameClasses = computed(() => {
  return {
    'tw-line-through': props.variant === ColorVariants.DANGER,
    'tw-opacity-30': hasNotStartedDownloading.value,
  }
})

const documentSizeClasses = computed(() => {
  return {
    'tw-opacity-30': hasNotStartedDownloading.value,
    'tw-w-[75px]': isActivelyDownloading.value,
  }
})

const hasDirectUploadDocument = computed(
  () =>
    isNotProposedDocument(props.document) &&
    isDirectUploadDocument(props.document)
)

const secondaryText = computed(() => {
  if (
    isNotProposedDocument(props.document) &&
    isDirectUploadDocument(props.document)
  ) {
    return formatBytes(props.document.size)
  }

  return null
})

const iconClass = computed(() => {
  switch (props.variant) {
    case ColorVariants.SUCCESS:
      return 'fa-check-circle'
    case ColorVariants.DANGER:
      return 'fa-solid fa-triangle-exclamation'
    default:
      return ''
  }
})

const isActivelyDownloading = computed(() => {
  return (
    hasDirectUploadDocument.value && props.progress && props.progress !== '100'
  )
})

const hasNotStartedDownloading = computed(() => {
  return hasDirectUploadDocument.value && !props.progress
})

const documentNameEllipsisLength = computed(() =>
  isActivelyDownloading.value ? 185 : 300
)

const documentSizeEllipsisLength = computed(() =>
  isActivelyDownloading.value ? 75 : 90
)
const documentRef = toRef(() => props.document)
const { viewingId, viewingUrl } = useDocumentView(documentRef)

function handleDownload() {
  if (!viewingId.value) {
    return
  }

  const url = window.Silverfin.Routes.file_document_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    viewingId.value.toString()
  )

  window.open(url)
}

function handleView() {
  window.open(viewingUrl.value, '_blank')
}

function handleDelete() {
  emit('delete', props.document.id)
}
</script>
